import { useEffect, useState } from "react";
import { Button, Empty, Input, Modal, Popconfirm, Skeleton, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { canEditNote, isPlanReadOnly } from "@utils/rtp_utils";
import { rtpApi, runRtpWrite, useCurrentUserUuid, useRtpNotes } from "./rtpHooks";
import type { RtpNote, RtpPlanDetail } from "./types";

const { TextArea } = Input;

interface NotesTabProps {
	plan: RtpPlanDetail;
}

interface EditNoteModalProps {
	note: RtpNote | null;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

const EditNoteModal = ({ note, open, onClose, refetch }: EditNoteModalProps) => {
	const [text, setText] = useState("");
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (open) setText(note?.text ?? "");
	}, [open, note]);

	const handleClose = () => {
		onClose();
	};

	const handleSubmit = async () => {
		if (!note) return;
		const trimmed = text.trim();
		if (!trimmed) return;
		setSubmitting(true);
		const ok = await runRtpWrite(() => rtpApi.patchRtpNote(note.id, { text: trimmed }), refetch);
		setSubmitting(false);
		if (ok) handleClose();
	};

	return (
		<Modal
			title="Edit Note"
			open={open}
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={submitting}
			destroyOnHidden
			okButtonProps={{ disabled: !text.trim(), "data-attr": "settings.rtp-note.edit-submit" } as any}
		>
			<TextArea rows={4} value={text} onChange={(e) => setText(e.target.value)} />
		</Modal>
	);
};

export const NotesTab = ({ plan }: NotesTabProps) => {
	const currentUserUuid = useCurrentUserUuid();
	const { notes, loading, refetch } = useRtpNotes(plan.id);
	const [newText, setNewText] = useState("");
	const [adding, setAdding] = useState(false);
	const [editingNote, setEditingNote] = useState<RtpNote | null>(null);

	const readOnly = isPlanReadOnly(plan);

	const handleAdd = async () => {
		const trimmed = newText.trim();
		if (!trimmed) return;
		setAdding(true);
		const ok = await runRtpWrite(
			() => rtpApi.rtpPlanAction(plan.id, { action: "ADD_NOTE", text: trimmed }),
			refetch
		);
		setAdding(false);
		if (ok) setNewText("");
	};

	const handleDelete = (note: RtpNote) => runRtpWrite(() => rtpApi.deleteRtpNote(note.id), refetch);

	return (
		<div>
			{readOnly ? null : (
				<div className="rtp-note-composer">
					<TextArea
						variant="borderless"
						autoSize={{ minRows: 2, maxRows: 6 }}
						placeholder="Add a note…"
						value={newText}
						onChange={(e) => setNewText(e.target.value)}
					/>
					<div className="rtp-note-composer-foot">
						<Button
							type="primary"
							data-attr="settings.rtp-note.add"
							disabled={!newText.trim()}
							loading={adding}
							onClick={handleAdd}
						>
							Add Note
						</Button>
					</div>
				</div>
			)}

			{loading && notes.length === 0 ? <Skeleton active /> : null}
			{!loading && notes.length === 0 ? <Empty description="No notes yet" /> : null}
			{notes.map((note) => {
				const editable = canEditNote(note, currentUserUuid) && !readOnly;
				return (
					<div key={note.id} className="rtp-note-card">
						<div className="rtp-note-main">
							<div className="rtp-note-head">
								<span className="rtp-note-author">{note.createdByName}</span>
								<span className="rtp-note-meta">
									{" · "}
									{dayjs(note.createdAt).tz("America/New_York").format("MMM D, YYYY h:mm A z")}
								</span>
							</div>
							<div className="rtp-note-text">{note.text}</div>
						</div>
						{editable ? (
							<div className="rtp-note-actions">
								<Tooltip title="Edit note">
									<Button
										type="text"
										size="small"
										icon={<EditOutlined />}
										aria-label="Edit note"
										data-attr="settings.rtp-note.edit"
										onClick={() => setEditingNote(note)}
									/>
								</Tooltip>
								<Popconfirm
									title="Delete this note?"
									description="This can't be undone."
									okText="Delete"
									okButtonProps={{ danger: true, "data-attr": "settings.rtp-note.delete-submit" } as any}
									onConfirm={() => handleDelete(note)}
								>
									<Tooltip title="Delete note">
										<Button
											type="text"
											danger
											size="small"
											icon={<DeleteOutlined />}
											aria-label="Delete note"
											data-attr="settings.rtp-note.delete"
										/>
									</Tooltip>
								</Popconfirm>
							</div>
						) : null}
					</div>
				);
			})}

			<EditNoteModal
				note={editingNote}
				open={editingNote !== null}
				onClose={() => setEditingNote(null)}
				refetch={refetch}
			/>
		</div>
	);
};
